import { inject, Injectable, Provider } from '@angular/core'

import { UserProfile, UserRole } from '@ti/contracts'
import { AuthSession } from '@ti/core/auth/services'
import { WhiteLabelSettingsProvider } from '@ti/core/common/services'
import { openLinkBlank } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { USER_DIRECTORY_EVENTS } from '@ti/core/contracts/user-directory-events'
import { BaseUserManager } from '@ti/shared/user/services'
import { UserDirectoryKind, UserStoreEventType } from '@ti/shared/user/stores'

import { FleetOwnerProvider } from '../../../app/fleet/models/fleet-owner.provider'
import { FleetAccountsManager } from '../../fleet/models/fleets.manager'

@Injectable()
export class TspUserManager extends BaseUserManager {
  protected readonly userDirectory = UserDirectoryKind.Tsp

  protected override readonly updateMessageSummaryKey: string = 'users.updated-message.detail'
  protected override readonly updateMessageDetailKey: string = ''
  protected override readonly deleteMessageSummaryKey: string = 'users.deleted-message.detail'
  protected override readonly deleteMessageDetailKey: string = ''

  public async sendInviteEmail(user: UserProfile) {
    return this.api.tsp.user.sendInviteEmail(user.id)
  }

  protected createUser(data: UserProfile) {
    return this.api.tsp.user.create(data)
  }

  protected override updateUser(user: UserProfile, data: Partial<UserProfile>): Promise<unknown> {
    return this.api.tsp.user.update(user.id, data)
  }

  protected async deleteUser(user: UserProfile) {
    return this.api.tsp.user.remove(user.id)
  }

  protected async disableUserMfa(user: UserProfile) {
    return this.api.tsp.user.disableMfa(user.id)
  }
}

@Injectable()
export class TspFleetUserManager extends BaseUserManager {
  protected readonly userDirectory = UserDirectoryKind.TspFleet

  protected override readonly updateMessageSummaryKey: string = 'users.updated-message.detail'
  protected override readonly updateMessageDetailKey: string = ''
  protected override readonly deleteMessageSummaryKey: string = 'users.deleted-message.detail'
  protected override readonly deleteMessageDetailKey: string = ''

  protected readonly appConfig = inject(CONFIG)
  protected readonly authSession = inject(AuthSession)
  protected readonly fleetAccountsManager = inject(FleetAccountsManager)
  protected readonly fleetOwnerProvider = inject(FleetOwnerProvider)
  protected readonly whiteLabelProvider = inject(WhiteLabelSettingsProvider)

  public override sendInviteEmail(user: UserProfile) {
    if (!user.fleetId) {
      throw new Error(`Fleet id is missing`)
    }
    return this.api.tsp.fleetUser.sendInviteEmail(user.fleetId, user.id)
  }

  public async loginAsFleetUser(user: UserProfile) {
    const idToken = await this.authSession.getIdToken(true)
    const session = await this.api.tsp.fleetUser.initLoginAsSession(user.fleetId, idToken, user.id)
    const whiteLabelSettings = await this.whiteLabelProvider.getData()
    const fleetWebUrl = `https://${
      whiteLabelSettings.domain ? whiteLabelSettings.domain : this.appConfig.fleetDefaultDomain
    }`
    openLinkBlank(`${fleetWebUrl}/?sessionId=${session.sessionId}`)
  }

  public async transferOwnership(user: UserProfile) {
    if (!user.fleetId) {
      throw new Error(`Fleet id is missing`)
    }

    const labels = await this.getTransferDialogLabels(
      user.name,
      this.fleetOwnerProvider.state.owner()?.name || '',
    )
    const dialogResult = await this.dialogFacade.confirm({
      ...labels,
    })
    if (dialogResult) {
      await this.api.tsp.fleetUser.setOwner(user.fleetId, user.id)
      this.fleetOwnerProvider.refresh()
      this.events$.updated.next()
      this.userStoreEvents.publish({
        type: UserStoreEventType.Invalidate,
        directory: this.userDirectory,
        fleetId: user.fleetId,
      })
      this.fleetAccountsManager.events.updated$.next([user.fleetId])
    }
  }

  protected async createUser(data: UserProfile) {
    if (!data.fleetId) {
      throw new Error(`Fleet id is missing`)
    }

    const result = await this.api.tsp.fleetUser.create(data.fleetId, data)
    if (data.roleId === UserRole.FleetOwner) {
      this.fleetOwnerProvider.refresh()
      this.userStoreEvents.publish({
        type: UserStoreEventType.Invalidate,
        directory: this.userDirectory,
        fleetId: data.fleetId,
      })
    }
    return result
  }

  protected override updateUser(user: UserProfile, data: Partial<UserProfile>): Promise<unknown> {
    if (!user.fleetId) {
      throw new Error(`Fleet id is missing`)
    }
    return this.api.tsp.fleetUser.update(user.fleetId, user.id, data)
  }

  protected async deleteUser(user: UserProfile) {
    if (!user.fleetId) {
      throw new Error(`Fleet id is missing`)
    }
    return this.api.tsp.fleetUser.remove(user.fleetId, user.id)
  }

  protected async disableUserMfa(user: UserProfile) {
    return this.api.tsp.fleetUser.disableMfa(user.fleetId, user.id)
  }

  protected async getTransferDialogLabels(newOwner: string, currentOwner: string) {
    return this.languageService.massTranslate(
      {
        summary: 'users.transfer-dialog.summary',
        description: 'users.transfer-dialog.description',
        confirmButton: 'users.transfer-dialog.confirm-button',
        cancelButton: 'button.cancel',
      },
      { newOwner, currentOwner },
    )
  }
}

export const provideTspUserManager = (): Provider[] => {
  return [
    TspUserManager,
    { provide: BaseUserManager, useExisting: TspUserManager },
    { provide: USER_DIRECTORY_EVENTS, useFactory: () => inject(TspUserManager).events$ },
  ]
}

export const provideTspFleetUserManager = (): Provider[] => {
  return [
    TspFleetUserManager,
    { provide: BaseUserManager, useExisting: TspFleetUserManager },
    { provide: USER_DIRECTORY_EVENTS, useFactory: () => inject(TspFleetUserManager).events$ },
  ]
}
